import { useCallback, useEffect, useMemo, useState } from 'react'
import { CalendarRange, ChevronDown, ChevronUp, Lock, X } from 'lucide-react'
import { supabase } from '../lib/supabase'
import {
  HIKE_AND_ABOVE, MONTH_INITIALS, MONTH_NAMES, REQUIRES_LABELS, REQUIRES_ORDER,
  areaPath, normaliseScores, polylinePoints, requiresFilterMatches, scoreTextColor,
  scoreTint, sortByScarcity,
} from '../lib/season'

const PLAN_COLUMNS = 'id,country_code,country_name,peak_name,elevation_m,stage,priority,season_scores,requires'

function isMissingSeasonColumns(error) {
  const message = String(error?.message || '').toLowerCase()
  return error?.code === '42703'
    || error?.code === 'PGRST204'
    || (message.includes('season_scores') && (message.includes('does not exist') || message.includes('could not find')))
}

function rowFromSummit(summit) {
  return {
    key: `summit-${summit.id}`,
    id: summit.id,
    table: 'summits',
    peak_name: summit.peak_name,
    country_name: summit.country_name,
    country_code: summit.country_code,
    flag: summit.flag,
    elevation_m: summit.elevation_m,
    requires: summit.requires,
    season_scores: summit.season_scores,
    badge: summit.status || 'future',
  }
}

function rowFromPlan(plan) {
  return {
    key: `plan-${plan.id}`,
    id: plan.id,
    table: 'country_plans',
    peak_name: plan.peak_name || 'Peak undecided',
    country_name: plan.country_name,
    country_code: plan.country_code,
    flag: '',
    elevation_m: plan.elevation_m,
    requires: plan.requires,
    season_scores: plan.season_scores,
    badge: plan.stage || 'idea',
  }
}

export default function SeasonMatrix({ open, onClose, summits = [], session, refresh }) {
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(false)
  const [migrationMissing, setMigrationMissing] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [threshold, setThreshold] = useState(50)
  const [activeRequires, setActiveRequires] = useState(HIKE_AND_ABOVE)
  const [sortMonth, setSortMonth] = useState(null)
  const [expandedKey, setExpandedKey] = useState(null)
  const [notice, setNotice] = useState('')

  const load = useCallback(async () => {
    if (!open || !supabase) return
    setLoading(true)
    setLoadError('')

    // Probe on summits: it is publicly readable, so this tells signed-out
    // visitors whether the migration has run without needing the backlog.
    const probe = await supabase.from('summits').select('id,season_scores').limit(1)
    if (probe.error) {
      setMigrationMissing(isMissingSeasonColumns(probe.error))
      if (!isMissingSeasonColumns(probe.error)) setLoadError(probe.error.message)
      setPlans([])
      setLoading(false)
      return
    }
    setMigrationMissing(false)

    if (!session) {
      setPlans([])
      setLoading(false)
      return
    }

    const { data, error } = await supabase.from('country_plans').select(PLAN_COLUMNS)
    if (error) {
      setPlans([])
      if (isMissingSeasonColumns(error)) setMigrationMissing(true)
      else setLoadError(error.message)
    } else {
      setPlans(data || [])
    }
    setLoading(false)
  }, [open, session])

  useEffect(() => { load() }, [load])

  const rows = useMemo(() => {
    // Completed peaks are history; this view is for deciding where to go next.
    const summitRows = summits
      .filter((summit) => summit.status !== 'completed')
      .map(rowFromSummit)
    // Built from ALL summits, so a completed country still suppresses a
    // duplicate backlog row for the same place.
    const trackedCodes = new Set(
      summits.map((summit) => String(summit.country_code || '').toUpperCase()).filter(Boolean),
    )
    // A country in the backlog that has since become a real summit target would
    // otherwise appear twice; the summit row wins.
    const planRows = plans
      .map(rowFromPlan)
      .filter((row) => !trackedCodes.has(String(row.country_code || '').toUpperCase()))
    return [...summitRows, ...planRows]
  }, [summits, plans])

  const filtered = useMemo(
    () => rows.filter((row) => requiresFilterMatches(activeRequires, row.requires)),
    [rows, activeRequires],
  )

  const ordered = useMemo(
    () => sortByScarcity(filtered, sortMonth, threshold),
    [filtered, sortMonth, threshold],
  )

  const withScores = ordered.filter((entry) => entry.scores).length

  if (!open) return null

  function toggleRequires(value) {
    setActiveRequires((current) => (current.includes(value)
      ? current.filter((entry) => entry !== value)
      : [...current, value]))
  }

  async function saveScores(entry, scores, requiresValue) {
    if (!supabase || !session) return
    const { row } = entry
    const { error } = await supabase
      .from(row.table)
      .update({ season_scores: scores, requires: requiresValue || null })
      .eq('id', row.id)
    if (error) {
      setNotice(error.message)
      return
    }
    setNotice(`${row.peak_name} updated.`)
    setExpandedKey(null)
    await load()
    if (row.table === 'summits') await refresh?.()
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <section className="admin-panel season-panel" role="dialog" aria-modal="true" aria-label="Seasonality matrix">
        <header className="admin-panel__header">
          <div>
            <div className="eyebrow">When to go</div>
            <h2>Seasonality</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close seasonality"><X size={19} /></button>
        </header>

        {migrationMissing && (
          <div className="route-upgrade-note season-note">
            <strong>Seasonality columns not ready</strong>
            <small>Run <code>supabase/seasonality-upgrade.sql</code>, then <code>supabase/seasonality-seed.sql</code>, once in the Supabase SQL editor and reopen this view.</small>
          </div>
        )}
        {loadError && <p className="place-search__error season-note">{loadError}</p>}
        {notice && <p className="season-notice">{notice}</p>}

        <div className="season-controls">
          <label className="season-threshold">
            <span>Minimum feasibility <em>{threshold}</em></span>
            <input
              type="range"
              min="0"
              max="90"
              step="10"
              value={threshold}
              onChange={(event) => setThreshold(Number(event.target.value))}
            />
          </label>

          <div className="season-requires" role="group" aria-label="Filter by what the peak requires">
            {REQUIRES_ORDER.map((value) => (
              <button
                type="button"
                key={value}
                className={activeRequires.includes(value) ? 'active' : ''}
                onClick={() => toggleRequires(value)}
                aria-pressed={activeRequires.includes(value)}
              >
                {REQUIRES_LABELS[value]}
              </button>
            ))}
          </div>
        </div>

        <div className="season-board">
          <div className="season-head">
            <div className="season-head__label">
              {sortMonth === null
                ? `${withScores} peaks with a curve`
                : `Sorted by what ${MONTH_NAMES[sortMonth]} is for`}
            </div>
            <div className="season-months">
              {MONTH_INITIALS.map((initial, index) => (
                <button
                  type="button"
                  key={`${initial}-${index}`}
                  className={sortMonth === index ? 'active' : ''}
                  onClick={() => setSortMonth((current) => (current === index ? null : index))}
                  aria-label={`Sort by ${MONTH_NAMES[index]}`}
                  aria-pressed={sortMonth === index}
                >
                  {initial}
                </button>
              ))}
            </div>
            <div className="season-gutter-head">{sortMonth === null ? '' : 'mths'}</div>
          </div>

          {loading && <div className="route-empty">Loading seasonality…</div>}

          {!loading && !ordered.length && (
            <div className="route-empty">
              {rows.length
                ? 'Nothing matches that filter. Try switching Walk back on.'
                : 'No peaks yet. Add summits, or run the seed SQL to load the European highpoints.'}
            </div>
          )}

          {ordered.map((entry) => {
            const scores = entry.scores
            const expanded = expandedKey === entry.row.key
            return (
              <div
                className={`season-row ${entry.belowThreshold ? 'season-row--dim' : ''}`}
                key={entry.row.key}
              >
                <div className="season-row__main">
                  <button
                    type="button"
                    className="season-row__label"
                    onClick={() => session && setExpandedKey(expanded ? null : entry.row.key)}
                    disabled={!session}
                    title={session ? 'Edit these scores' : undefined}
                  >
                    <strong>{entry.row.flag ? `${entry.row.flag} ` : ''}{entry.row.peak_name}</strong>
                    <small>
                      {[
                        entry.row.country_name,
                        entry.row.elevation_m ? `${Number(entry.row.elevation_m).toLocaleString()} m` : '',
                        REQUIRES_LABELS[entry.row.requires] || 'Unrated',
                        entry.row.badge,
                      ].filter(Boolean).join(' · ')}
                    </small>
                    {session && <span className="season-row__chevron">{expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}</span>}
                  </button>

                  <div className="season-cells">
                    {MONTH_INITIALS.map((initial, index) => (
                      <div
                        key={`${entry.row.key}-${index}`}
                        className={`season-cell ${sortMonth === index ? 'season-cell--selected' : ''}`}
                        style={{
                          background: scores ? scoreTint(scores[index], threshold) : 'transparent',
                          color: scores ? scoreTextColor(scores[index], threshold) : 'inherit',
                        }}
                        title={scores ? `${MONTH_NAMES[index]}: ${scores[index]}` : `${MONTH_NAMES[index]}: no data`}
                      >
                        {scores ? scores[index] : ''}
                      </div>
                    ))}
                    {scores && (
                      <svg
                        className="season-curve"
                        viewBox="0 0 12 100"
                        preserveAspectRatio="none"
                        aria-hidden="true"
                      >
                        <path d={areaPath(scores)} className="season-curve__area" />
                        <polyline points={polylinePoints(scores)} className="season-curve__line" vectorEffect="non-scaling-stroke" />
                      </svg>
                    )}
                  </div>

                  <div className="season-gutter">
                    {sortMonth !== null && scores ? entry.feasibleMonths : ''}
                  </div>
                </div>

                {expanded && (
                  <SeasonScoreEditor
                    entry={entry}
                    onCancel={() => setExpandedKey(null)}
                    onSave={(nextScores, nextRequires) => saveScores(entry, nextScores, nextRequires)}
                  />
                )}
              </div>
            )
          })}
        </div>

        {!session && (
          <p className="season-signin-note">
            <Lock size={13} /> Backlog countries are private — sign in from Manage to include them and to edit scores.
          </p>
        )}
        <p className="season-footnote">
          <CalendarRange size={13} /> Scores are estimates of the chance of a summit in a normal year, not a forecast.
        </p>
      </section>
    </div>
  )
}

function SeasonScoreEditor({ entry, onSave, onCancel }) {
  const existing = normaliseScores(entry.row.season_scores)
  const [draft, setDraft] = useState(() => (existing || Array.from({ length: 12 }, () => 0)).map(String))
  const [requiresValue, setRequiresValue] = useState(entry.row.requires || 'hike')

  function update(index, value) {
    setDraft((current) => current.map((entryValue, entryIndex) => (entryIndex === index ? value : entryValue)))
  }

  function submit(event) {
    event.preventDefault()
    const scores = draft.map((value) => Math.min(100, Math.max(0, Math.round(Number(value) || 0))))
    onSave(scores, requiresValue)
  }

  return (
    <form className="season-editor" onSubmit={submit}>
      <div className="season-editor__inputs">
        {MONTH_INITIALS.map((initial, index) => (
          <label key={`edit-${index}`}>
            <span>{initial}</span>
            <input
              type="number"
              min="0"
              max="100"
              value={draft[index]}
              onChange={(event) => update(index, event.target.value)}
              aria-label={`${MONTH_NAMES[index]} feasibility for ${entry.row.peak_name}`}
            />
          </label>
        ))}
      </div>
      <div className="season-editor__actions">
        <label>
          Requires
          <select value={requiresValue} onChange={(event) => setRequiresValue(event.target.value)}>
            {REQUIRES_ORDER.map((value) => (
              <option key={value} value={value}>{REQUIRES_LABELS[value]}</option>
            ))}
          </select>
        </label>
        <button type="button" className="secondary-button" onClick={onCancel}>Cancel</button>
        <button className="primary-button">Save scores</button>
      </div>
    </form>
  )
}
